<template>
    <div class="w-full">
        <div class="inline">
            <page-headline>{{$t('pages.insights.audience.insights_users')}}</page-headline>
        </div>
        <div class="mb-4 flex items-center p-3 w-full bg-gray-200">
            <search-filter v-model="form.search" class="max-w-md mr-4">
                <label class="mt-2 block py-1 pl-3 bg-ezdBlue text-white">{{$t('pages.insights.audience.type')}}</label>
                <select v-model="form.type" class="w-full form-select">
                    <option value="-1">{{$t('all.auction_types.all')}}</option>
                    <option value="3-L">{{$t('all.auction_types.livestyle')}}</option>
                    <option value="3-P">{{$t('all.auction_types.secretsale')}}</option>
                </select>
                <label class="mt-2 block py-1 pl-2 bg-ezdBlue text-white">{{$t('pages.insights.audience.time_period')}}</label>
                <div class="border border-t-0 py-2">
                    <div class="w-full flex items-center">
                        <div class="w-2/12 justify-center pl-3">{{$t('pages.insights.audience.from')}}</div>
                        <div class="w-5/12">
                            <input class="text-gray-700 text-md bg-gray-200 p-1 right-0" type="date" name="dateStart" v-model="form.dateStart"/>
                        </div>
                        <div class="w-5/12">
                            <input class="text-gray-700 text-md bg-gray-200 p-1 right-0" type="time" name="timeStart" v-model="form.timeStart"/>
                        </div>
                    </div>
                    <div class="w-full flex items-center">
                        <div class="w-2/12 justify-center pl-3">{{$t('pages.insights.audience.to')}}</div>
                        <div class="w-5/12">
                            <input class="text-gray-700 text-md bg-gray-200 p-1 right-0" type="date" name="dateEnd" v-model="form.dateEnd"/>
                        </div>
                        <div class="w-5/12">
                            <input class="text-gray-700 text-md bg-gray-200 p-1 right-0" type="time" name="timeEnd" v-model="form.timeEnd"/>
                        </div>
                    </div>
                </div>
            </search-filter>
            <div class="inline-block flex text-nowrap text-sm justify-center mr-3 pl-3 border-l border-gray-500">
                {{$t('pages.insights.audience.grouped_by')}}
            </div>
            <div class="relative">
                <select v-model="form.group" class="inline-block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 py-3 px-4 pr-8 leading-tight focus:outline-none focus:shadow-outline">
                    <option v-for="group in groups" :value="group.id" :model="groups.time">{{ group.label }}</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
            </div>
        </div>
        <div class="w-full pt-5">
            <h2 class="text-center mb-3 text-lg">{{ selectedGroup.text }}</h2>
            <div class="text-center text-sm" v-if="!this.hasData">{{$t('all.no_data_for_selected_period')}}</div>
            <bar
                :labels="this.labels"
                :data="this.data"
                :options="this.chartOptions"
                :height="500"
                v-if="this.hasData"
            />
        </div>
    </div>
</template>

<script>
    import Layout from '@/Shared/Layout';
    import Pagination from '@/Shared/Pagination';
    import Icon from '@/Shared/Icon';
    import PageHeadline from "@/Shared/PageHeadline";
    import Status from '@/Shared/Status';
    import pickBy from 'lodash/pickBy';
    import throttle from 'lodash/throttle';
    import AuctionState from "@/mixins/AuctionState";
    import SearchFilter from "@/Shared/SearchFilter";
    import Bar from "@/Shared/Charts/Bar";
    export default {
        metaInfo() {
            return {
                title: this.$t('pages.insights.audience.insights_users')
            }
        },
        mixins: [AuctionState],
        layout: Layout,
        remember: 'form',
        components: {
            Pagination,
            Icon,
            PageHeadline,
            Status,
            SearchFilter,
            Bar
        },
        props: {
            stats: Object,
            settings: Object
        },
        data() {
            return {
                form: {
                    dateStart: this.settings.dateStart,
                    timeStart: this.settings.timeStart,
                    dateEnd: this.settings.dateEnd,
                    timeEnd: this.settings.timeEnd,
                    group: 'day',
                    type: -1
                },
                colors: [
                    '#9ca3af',
                    '#6b7280',
                    '#475569',
                    '#374151',
                    '#1f2937'
                ],
                groups: [
                    {
                        'id': 'minute',
                        'label': this.$t('pages.insights.audience.minute_label'),
                        'text': this.$t('pages.insights.audience.minute_text')
                    },
                    {
                        'id': 'quarter',
                        'label': this.$t('pages.insights.audience.quarter_label'),
                        'text': this.$t('pages.insights.audience.quarter_text')
                    },
                    {
                        'id': 'half',
                        'label': this.$t('pages.insights.audience.half_label'),
                        'text': this.$t('pages.insights.audience.half_text')
                    },
                    {
                        'id': 'day-hour',
                        'label': this.$t('pages.insights.audience.hour_label'),
                        'text': this.$t('pages.insights.audience.hour_text')
                    },
                    {
                        'id': 'day',
                        'label': this.$t('pages.insights.audience.day_label'),
                        'text': this.$t('pages.insights.audience.day_text')
                    },
                    {
                        'id': 'week',
                        'label': this.$t('pages.insights.audience.week_label'),
                        'text': this.$t('pages.insights.audience.week_text')
                    },
                    {
                        'id': 'month',
                        'label': this.$t('pages.insights.audience.month_label'),
                        'text': this.$t('pages.insights.audience.month_text')
                    }
                ]
            }
        },
        methods: {
            isDate(value) {
                var date = Date.parse(value);
                return !isNaN(date);
            },
            mapAuctionTypeName(auctionTypeId, auctionType) {
                switch (auctionTypeId) {
                    case 1:
                        return ('P' === auctionType) ? this.$t('all.auction_names.parallel_auction') : this.$t('all.auction_names.daily_auction') ;
                    case 2:
                        return ('P' === auctionType) ? this.$t('all.auction_names.parallel_auction_bid_up') : ('T' === auctionType ? this.$t('all.auction_names.daily_auction_bid_up') : this.$t('all.auction_names.oba'));
                    case 3:
                        return ('L' === auctionType) ? this.$t('all.auction_names.livestyle') : ('P' === auctionType ? this.$t('all.auction_names.secretsale') : this.$t('all.auction_names.custom'));
                }
            },
        },
        computed: {
            selectedGroup() {
              return this.groups.filter((group) => group.id === this.form.group)[0];
            },
            hasData() {
              return this.channels.length > 0;
            },
            channels() {
                return Object.keys(this.stats);
            },
            labels() {
                let result = [];
                this.channels.map((channel) => {
                    return this.stats[channel].map(({label}) => result.push(label));
                });
                // Make the labels unique
                return [...new Set(result)];
            },
            datasets() {
                return [
                    ...
                    this.channels.map((channel, index) => {
                        return {
                            responsive: true,
                            backgroundColor: this.colors[index],
                            label: this.mapAuctionTypeName(this.stats[channel][0].auction_type_id, this.stats[channel][0].type),
                            fill: false,
                            data: [
                                ...
                                this.stats[channel].map((data) => ({'x': data.label, 'y': parseInt(data.value)}))
                            ]
                        }
                    })
                ]
            },
            data() {
                return {
                    datasets: this.datasets,
                    labels: this.labels
                }
            },
            chartOptions() {
                return {
                    maintainAspectRatio: false,
                    tooltips: {
                        callbacks: {
                            title: function() {}
                        }
                    },
                    scales: {
                        xAxes: [{
                            ticks: {
                                autoSkip: false,
                                minRotation: 90
                            }
                        }],
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                suggestedMin: 0,
                                stepSize: 1
                            }
                        }]
                    }
                }
            },
        },
        watch: {
            form: {
                handler: throttle(function() {
                    let query = pickBy(this.form);
                    this.$inertia.get(this.route('insights.audience', Object.keys(query).length ? query : {}), {}, { preserveState: true })
                }, 150),
                deep: true
            }
        },
    }
</script>
